import { AnsweringClient, PaginationParams } from "../../../../clients/answeringClient";
import { describeApiError, isExpiredSession } from "../../../../utils/apiError";

export interface IAssignment {
  applicationId: number;
  title: string;
  dueDate: Date;
  instrumentTitle: string;
}

export interface AddInstrumentModalControllerDelegate {
  didGetAssignments: (assignments: IAssignment[]) => void;
  didFailGetAssignments: (message: string) => void;
  /**
   * `emailDelivered` is `false` only when a notification was owed and none was
   * delivered; the assignment and its licences stand regardless.
   */
  didAddAssignments: (emailDelivered: boolean | null) => void;
  didFailAddAssignments: (message: string) => void;
  applicationShouldLogout: () => void;
  insufficientLicensesError: () => void;
}

export class AddInstrumentModalController {
  constructor(
    private delegate: AddInstrumentModalControllerDelegate,
    private readonly answeringClient = new AnsweringClient()
  ) {}

  public async getManagedAssignments(
    accessToken: string,
    options?: PaginationParams,
    hasRetried = false
  ): Promise<void> {
    try {
      const response = await this.answeringClient.getManagedAssignments(
        accessToken,
        options
      );
      const data = response.data.data as IAssignment[];
      const now = new Date();
      const filteredData = data.filter((assignment) => {
        return new Date(assignment.dueDate) > now;
      });
      this.delegate.didGetAssignments(filteredData);
    } catch (error: any) {
      if (isExpiredSession(error) && !hasRetried) {
        const newTokens = await this.refreshTokens();
        if (!newTokens) {
          return;
        }
        return this.getManagedAssignments(newTokens.accessToken, options, true);
      }
      this.delegate.didFailGetAssignments(
        describeApiError(
          error,
          "Não foi possível carregar as aplicações disponíveis."
        )
      );
    }
  }

  public async addAssignmentsToUser(
    accessToken: string,
    userUid: string,
    assignmentIds: number[],
    hasRetried = false
  ): Promise<void> {
    try {
      const response = await this.answeringClient.addAssignmentsToUser(
        accessToken,
        userUid,
        assignmentIds
      );
      // Absent on an older backend that still answers 204 — reads as `null`.
      this.delegate.didAddAssignments(response.data?.emailDelivered ?? null);
    } catch (error: any) {
      if (error?.response?.status === 402) {
        this.delegate.insufficientLicensesError();
        return;
      }
      if (isExpiredSession(error) && !hasRetried) {
        const newTokens = await this.refreshTokens();
        if (!newTokens) {
          return;
        }
        return this.addAssignmentsToUser(
          newTokens.accessToken,
          userUid,
          assignmentIds,
          true
        );
      }
      this.delegate.didFailAddAssignments(
        describeApiError(
          error,
          "Não foi possível adicionar as aplicações. Tente novamente em instantes."
        )
      );
    }
  }

  private async refreshTokens(): Promise<{
    accessToken: string;
    refreshToken: string;
  } | void> {
    const refreshToken = localStorage.getItem("RefreshToken");
    if (!refreshToken) {
      this.delegate.applicationShouldLogout();
      return;
    }
    try {
      const response = await this.answeringClient.refreshToken(refreshToken);
      const tokens: { accessToken: string; refreshToken: string } =
        response.data;
      localStorage.setItem("AccessToken", tokens.accessToken);
      localStorage.setItem("RefreshToken", tokens.refreshToken);
      return tokens;
    } catch (error: any) {
      this.delegate.applicationShouldLogout();
      return;
    }
  }
}
